// Preview helpers copied from KL-Engraving-App/extension/panel.js.
const FAMILY = {
  Montserrat: "Montserrat, sans-serif",
  "Cormorant Garamond": "'Cormorant Garamond', serif",
  "Petit Formal Script": "'Petit Formal Script', cursive",
};
const ZOOM_MAX = 2.9;
const FIT = 0.72;
const imageCache = new Map();

export function layout(ctx, text, track = 0) {
  const characters = String(text || "").split("");
  const widths = characters.map((character) => ctx.measureText(character).width);
  const total = widths.reduce((sum, width) => sum + width, 0) + Math.max(0, characters.length - 1) * track;
  const glyphs = [];
  let x = -total / 2;
  for (let index = 0; index < characters.length; index += 1) {
    glyphs.push({ ch: characters[index], x });
    x += widths[index] + track;
  }
  return { glyphs, width: total };
}

export function paintDeboss(ctx, line, fontPx) {
  const unit = Math.max(0.5, fontPx * 0.028);
  const pass = (dx, dy) => {
    for (const glyph of line.glyphs) ctx.fillText(glyph.ch, glyph.x + dx, dy);
  };
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = "rgba(0,0,0,0.20)";
  ctx.shadowColor = "rgba(0,0,0,0.18)";
  ctx.shadowBlur = unit * 1.2;
  ctx.shadowOffsetX = unit * 0.5;
  ctx.shadowOffsetY = unit * 0.5;
  pass(0, 0);
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.globalCompositeOperation = "lighter";
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  pass(-unit * 0.5, -unit * 0.7);
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = "#000000";
  pass(0, 0);
  ctx.globalAlpha = 1;
}

function imageFor(url) {
  if (!url) return null;
  if (!imageCache.has(url)) {
    const image = new Image();
    image.src = url;
    imageCache.set(url, image);
  }
  return imageCache.get(url);
}

export function drawPreview(canvas, text, fontName, placement, imageUrl) {
  const image = imageFor(imageUrl);
  if (!image || !placement) return;
  if (!image.complete || !image.naturalWidth) {
    if (!image.__engraveLoadListener) {
      image.__engraveLoadListener = true;
      image.addEventListener("load", () => drawPreview(canvas, text, fontName, placement, imageUrl), { once: true });
    }
    return;
  }
  const box = canvas.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.round((box.width || 360) * dpr);
  const height = Math.round(width / 2);
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = canvas.getContext("2d");
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);
  const family = FAMILY[fontName] ?? FAMILY.Montserrat;
  const imageWidth = image.naturalWidth;
  const imageHeight = image.naturalHeight;
  let markWidth = 0;
  let markHeight = 0;
  let fontScale = 1;
  if (text) {
    const fontPx = placement.font * canvasWidth;
    ctx.font = `${fontPx}px ${family}`;
    const measured = layout(ctx, text, 0);
    markWidth = measured.width / canvasWidth;
    markHeight = placement.font * 1.2;
  }
  const bandWidth = placement.safeR - placement.safeL;
  const bandHeight = (placement.safeB - placement.safeT) * (imageHeight / imageWidth);
  let centerX = placement.x;
  let centerY = placement.y;
  if (markWidth > 0 && bandWidth > 0 && bandHeight > 0) {
    const fit = Math.min(1, bandWidth / markWidth, bandHeight / markHeight);
    if (fit < 1) {
      fontScale *= fit;
      markWidth *= fit;
      markHeight *= fit;
    }
    const halfWidth = markWidth / 2;
    const halfHeight = (markHeight * (imageWidth / imageHeight)) / 2;
    centerX = Math.min(Math.max(placement.x, placement.safeL + halfWidth), placement.safeR - halfWidth);
    centerY = Math.min(Math.max(placement.y, placement.safeT + halfHeight), placement.safeB - halfHeight);
  }
  const zoom = markWidth > 0 ? Math.max(1, Math.min(ZOOM_MAX, FIT / markWidth)) : ZOOM_MAX;
  const sourceWidth = imageWidth / zoom;
  const sourceHeight = sourceWidth * (canvasHeight / canvasWidth);
  const halfY = sourceHeight / imageHeight / 2;
  const halfX = sourceWidth / imageWidth / 2;
  const markHalfY = (markHeight * (imageWidth / imageHeight)) / 2;
  const cropMin = halfY;
  const cropMax = 1 - halfY;
  const cropLow = centerY + markHalfY - halfY + 0.01;
  const cropHigh = placement.safeB + 0.03 - halfY;
  const lowY = Math.max(cropLow, cropMin);
  const highY = Math.max(lowY, Math.min(cropHigh, cropMax));
  const cropCenterY = Math.min(Math.max(centerY, lowY), highY);
  const cropCenterX = sourceWidth >= imageWidth
    ? 0.5
    : Math.min(Math.max(centerX, halfX), Math.max(halfX, 1 - halfX));
  const sourceX = imageWidth * cropCenterX - sourceWidth / 2;
  const sourceY = imageHeight * cropCenterY - sourceHeight / 2;
  ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvasWidth, canvasHeight);
  if (!text) return;
  const x = ((imageWidth * centerX - sourceX) / sourceWidth) * canvasWidth;
  const y = ((imageHeight * centerY - sourceY) / sourceHeight) * canvasHeight;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((placement.rot * Math.PI) / 180);
  const fontPx = placement.font * canvasWidth * zoom * fontScale;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.font = `${fontPx}px ${family}`;
  paintDeboss(ctx, layout(ctx, text, 0), fontPx);
  ctx.restore();
}
